import { useEffect, useRef, useState } from "react";
import { useSiteContent } from "@/cms/SiteContentContext";
import { editableAttrs } from "@/cms/previewUtils";

const HeroSection = () => {
  const [loaded, setLoaded] = useState(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const { content } = useSiteContent();
  const hero = content.home.hero;

  useEffect(() => {
    const timer = setTimeout(() => setLoaded(true), 100);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const tryPlay = () => {
      void video.play().catch(() => {});
    };

    const handleVisibility = () => {
      if (!document.hidden) tryPlay();
    };

    tryPlay();
    document.addEventListener("visibilitychange", handleVisibility);
    const monitor = window.setInterval(() => {
      if (!document.hidden && video.paused) tryPlay();
    }, 2000);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibility);
      window.clearInterval(monitor);
    };
  }, []);

  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section id="home" className="relative min-h-[100svh] flex flex-col pt-16 md:pt-20" {...editableAttrs("home.hero", "object")}>
      <div className="absolute inset-0 z-0 overflow-hidden">
        <video
          ref={videoRef}
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          disablePictureInPicture
          controlsList="nodownload nofullscreen noplaybackrate noremoteplayback"
          className="hero-bg-video w-full h-full object-cover"
          {...editableAttrs("home.hero.videoSrc", "video")}
        >
          <source src={hero.videoSrc} type="video/mp4" {...editableAttrs("home.hero.videoSrc", "video")} />
        </video>
        <div className="absolute inset-0 pointer-events-none bg-gradient-to-r from-zinc-950/90 via-zinc-950/70 to-zinc-950/40"></div>
        <div className="absolute inset-0 pointer-events-none bg-gradient-to-t from-zinc-950/80 via-transparent to-zinc-950/30"></div>
      </div>

      <div className="absolute left-0 top-0 h-full w-1 bg-red-600 z-10"></div>

      <div className="relative z-10 flex-1 flex items-center">
        <div className="max-w-7xl mx-auto px-6 w-full py-10 md:py-12">
          <div
            className="max-w-3xl"
            style={{
              opacity: loaded ? 1 : 0,
              transform: loaded ? "translateY(0)" : "translateY(30px)",
              transition: "opacity 0.8s ease, transform 0.8s ease",
            }}
          >
            <div className="flex flex-wrap items-center gap-3 mb-5">
              <span className="w-8 h-0.5 bg-red-600"></span>
              <span className="text-red-500 text-sm font-semibold tracking-widest uppercase" style={{ fontFamily: "Rajdhani, sans-serif" }} {...editableAttrs("home.hero.badge", "text")}>
                {hero.badge}
              </span>
              <span
                className="text-white text-sm md:text-base font-bold tracking-wider px-3.5 py-1.5 border border-red-500/60 bg-red-600/20 shadow-[0_0_20px_rgba(220,38,38,0.2)]"
                style={{ fontFamily: "Rajdhani, sans-serif" }}
                {...editableAttrs("home.hero.distributorBadge", "text")}
              >
                {hero.distributorBadge}
              </span>
            </div>

            <h1 className="text-4xl sm:text-5xl md:text-7xl font-bold text-white leading-tight mb-6" style={{ fontFamily: "Rajdhani, sans-serif", lineHeight: 1.05 }}>
              <span {...editableAttrs("home.hero.titleLines.0", "text")}>{hero.titleLines[0]}</span>
              <br />
              <span className="text-red-600" {...editableAttrs("home.hero.titleLines.1", "text")}>
                {hero.titleLines[1]}
              </span>
              <br />
              <span {...editableAttrs("home.hero.titleLines.2", "text")}>{hero.titleLines[2]}</span>
            </h1>

            <p className="text-zinc-300 text-sm sm:text-base md:text-lg leading-relaxed mb-6 sm:mb-8 max-w-xl" {...editableAttrs("home.hero.description", "textarea")}>
              {hero.description}
            </p>
            <p className="text-zinc-400 text-xs sm:text-sm md:text-base leading-relaxed mb-8 max-w-xl" {...editableAttrs("home.hero.subDescription", "textarea")}>
              {hero.subDescription}
            </p>

            <div className="flex flex-col sm:flex-row flex-wrap gap-3 sm:gap-4">
              <button
                onClick={() => scrollTo("services")}
                className="w-full sm:w-auto justify-center flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-7 py-3.5 font-bold text-sm tracking-widest transition-all duration-200 cursor-pointer whitespace-nowrap"
                style={{ fontFamily: "Rajdhani, sans-serif" }}
                {...editableAttrs("home.hero.servicesButtonLabel", "text")}
              >
                <i className="ri-tools-line text-base"></i>
                {hero.servicesButtonLabel}
              </button>
              <button
                onClick={() => scrollTo("contact")}
                className="w-full sm:w-auto justify-center flex items-center gap-2 border border-white/40 hover:border-red-600 text-white hover:text-red-500 px-7 py-3.5 font-bold text-sm tracking-widest transition-all duration-200 cursor-pointer whitespace-nowrap"
                style={{ fontFamily: "Rajdhani, sans-serif" }}
                {...editableAttrs("home.hero.appointmentButtonLabel", "text")}
              >
                <i className="ri-phone-line text-base"></i>
                {hero.appointmentButtonLabel}
              </button>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-6 mt-12 sm:mt-14 pt-8 sm:pt-10 border-t border-white/10" {...editableAttrs("home.hero.stats", "array")}>
              {hero.stats.map((stat, idx) => (
                <div key={`${stat.label}-${idx}`}>
                  <div className="text-3xl md:text-4xl font-bold text-red-500" style={{ fontFamily: "Rajdhani, sans-serif" }}>
                    {stat.value}
                  </div>
                  <div className="text-zinc-400 text-sm mt-1">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <button
        onClick={() => scrollTo("services")}
        className="absolute bottom-28 right-8 hidden lg:flex flex-col items-center gap-2 text-zinc-500 hover:text-white transition-colors cursor-pointer z-10"
      >
        <span className="text-xs tracking-widest uppercase" style={{ fontFamily: "Rajdhani, sans-serif", writingMode: "vertical-rl" }} {...editableAttrs("home.hero.scrollLabel", "text")}>
          {hero.scrollLabel}
        </span>
        <i className="ri-arrow-down-line text-lg animate-bounce"></i>
      </button>
    </section>
  );
};

export default HeroSection;
